import { useCallback, useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useLingui } from '@lingui/react'
import { msg } from '@lingui/macro'
import SvgCustomIcon from '../icons/SvgCustomIcon'
import { resolveConfigDisplayLabel } from '../../utils/config-localization'

const INFO_ICON_SIZE = 16

/**
 * Etichetta campo form con icona info opzionale (tooltip da configurazione).
 * @param {object} props
 * @param {string} props.label
 * @param {string} [props.tooltip]
 * @param {boolean} [props.isMobile]
 * @param {import('@mui/material').Theme} props.theme
 */
export function FormFieldLabel({ label, tooltip, isMobile = false, theme }) {
  const { _ } = useLingui()
  const [helpOpen, setHelpOpen] = useState(false)
  const [hoverOpen, setHoverOpen] = useState(false)

  const openHelp = useCallback((e) => {
    if (!tooltip) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    setHelpOpen(true)
  }, [tooltip])

  const closeHelp = useCallback(() => {
    setHelpOpen(false)
    setHoverOpen(false)
  }, [])

  const tooltipOpen = !isMobile && (helpOpen || hoverOpen)

  return (
    <>
      <Box
        component="span"
        display="inline-flex"
        alignItems="center"
        gap={0.5}
        sx={{
          maxWidth: 'none',
          overflow: 'visible',
          flexWrap: 'nowrap',
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: 'visible',
            textOverflow: 'clip',
            ...(tooltip ? { cursor: 'pointer' } : {}),
          }}
          onClick={tooltip ? openHelp : undefined}
        >
          {label}
        </Box>
        {tooltip ? (
          isMobile ? (
            <Box
              component="span"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              onClick={openHelp}
              sx={{
                lineHeight: 0,
                cursor: 'pointer',
                pointerEvents: 'auto',
                verticalAlign: 'middle',
                minWidth: 32,
                minHeight: 32,
                ml: -0.5,
              }}
            >
              <SvgCustomIcon
                name="info"
                size={INFO_ICON_SIZE}
                color={theme.palette.newColors.gray}
              />
            </Box>
          ) : (
            <Tooltip
              title={tooltip}
              arrow
              placement="top"
              describeChild
              open={tooltipOpen}
              onClose={closeHelp}
              disableFocusListener
              disableTouchListener
            >
              <Box
                component="span"
                display="inline-flex"
                alignItems="center"
                flexShrink={0}
                onMouseEnter={() => setHoverOpen(true)}
                onMouseLeave={() => setHoverOpen(false)}
                onClick={openHelp}
                sx={{
                  lineHeight: 0,
                  cursor: 'help',
                  pointerEvents: 'auto',
                  verticalAlign: 'middle',
                }}
              >
                <SvgCustomIcon
                  name="info"
                  size={INFO_ICON_SIZE}
                  color={theme.palette.newColors.gray}
                />
              </Box>
            </Tooltip>
          )
        ) : null}
      </Box>
      {tooltip && isMobile ? (
        <Dialog
          open={helpOpen}
          onClose={closeHelp}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              m: 2,
              borderRadius: 2,
              maxWidth: 340,
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              py: 1.25,
              px: 2,
              pr: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ pt: 0.25, flexShrink: 0, lineHeight: 0 }}>
              <SvgCustomIcon
                name="info"
                size={18}
                color={theme.palette.newColors.pink}
              />
            </Box>
            <Typography
              component="span"
              sx={{
                flex: 1,
                fontSize: '0.8125rem',
                fontWeight: 600,
                lineHeight: 1.35,
                color: theme.palette.newColors.gray,
              }}
            >
              {label}
            </Typography>
            <IconButton
              aria-label={_(msg`Close`)}
              onClick={closeHelp}
              size="small"
              sx={{ mt: -0.5, mr: -0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 2 } }}>
            <Typography
              sx={{
                fontSize: '0.8125rem',
                lineHeight: 1.5,
                color: 'text.secondary',
                whiteSpace: 'pre-wrap',
              }}
            >
              {tooltip}
            </Typography>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  )
}

/**
 * @param {{ label?: string, tooltip?: string }} elem
 * @param {boolean} isMobile
 * @param {import('@mui/material').Theme} theme
 */
export function renderFormFieldLabel(elem, isMobile, theme) {
  return (
    <FormFieldLabel
      label={resolveConfigDisplayLabel(elem)}
      tooltip={elem.tooltip}
      isMobile={isMobile}
      theme={theme}
    />
  )
}
